import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Car, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Expense } from "@/hooks/useExpenses";
import { Vehicle } from "@/hooks/useVehicles";
import { Trip } from "@/hooks/useTrips";
import { useMemo } from "react";

interface ExpenseSummaryCardsProps {
  expenses: Expense[];
  vehicles: Vehicle[];
  trips: Trip[];
  selectedVehicleId?: string | null;
  onVehicleClick?: (vehicleId: string) => void;
}

const MAINTENANCE_CATEGORIES = ["Maintenance", "Repairs", "Tires", "Oil Change", "Detailing"];

export const ExpenseSummaryCards = ({ expenses, vehicles, trips, selectedVehicleId, onVehicleClick }: ExpenseSummaryCardsProps) => {
  const totalExpenses = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  const vehicleExpenses = expenses.filter((e) => e.vehicle_id);
  const uniqueVehicleIds = new Set(vehicleExpenses.map((e) => e.vehicle_id));
  const avgPerCar = uniqueVehicleIds.size > 0 ? totalExpenses / uniqueVehicleIds.size : 0;

  const operationsExpenses = expenses
    .filter((e) => !e.vehicle_id)
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);

  // Miles driven per vehicle from trips
  const milesByVehicle = useMemo(() => {
    const map: Record<string, number> = {};
    trips.forEach((t) => {
      if (t.vehicle_id && t.distance_traveled && !t.trip_status?.toLowerCase().includes("cancel")) {
        map[t.vehicle_id] = (map[t.vehicle_id] || 0) + t.distance_traveled;
      }
    });
    return map;
  }, [trips]);

  // Per-vehicle totals
  const vehicleTotals = useMemo(() => {
    return vehicles.map((v) => {
      const vExpenses = expenses.filter((e) => e.vehicle_id === v.id);
      const total = vExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
      const maintenanceTotal = vExpenses
        .filter((e) => MAINTENANCE_CATEGORIES.includes(e.expense_type || ""))
        .reduce((sum, e) => sum + Number(e.amount || 0), 0);
      const expenseCount = vExpenses.length;
      const miles = milesByVehicle[v.id] || 0;
      const maintenanceCostPerMile = miles > 0 ? maintenanceTotal / miles : null;
      return { vehicle: v, total, expenseCount, maintenanceCostPerMile };
    }).filter((vt) => vt.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [vehicles, expenses, milesByVehicle]);

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <Card className="border-border/50 bg-gradient-to-br from-card to-card/80">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Expenses</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{fmt(totalExpenses)}</div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-gradient-to-br from-card to-card/80">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Avg Expenses per Car</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{fmt(avgPerCar)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {uniqueVehicleIds.size} vehicle{uniqueVehicleIds.size !== 1 ? "s" : ""}
            </p>
          </CardContent>
        </Card>

        <Card
          className={`border-border/50 bg-gradient-to-br from-card to-card/80 cursor-pointer transition-all ${
            selectedVehicleId === "operations"
              ? "ring-2 ring-primary bg-primary/5"
              : "hover:bg-muted/50"
          }`}
          onClick={() => onVehicleClick?.("operations")}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Operations Expenses</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{fmt(operationsExpenses)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Not tied to a specific vehicle
            </p>
          </CardContent>
        </Card>
      </div>

      {vehicleTotals.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <Car className="h-5 w-5 text-primary" />
            Expenses by Vehicle
          </h2>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {vehicleTotals.map(({ vehicle, total, expenseCount, maintenanceCostPerMile }) => (
              <Card
                key={vehicle.id}
                className={`cursor-pointer transition-all ${
                  selectedVehicleId === vehicle.id
                    ? "ring-2 ring-primary bg-primary/5"
                    : "hover:bg-muted/50"
                }`}
                onClick={() => onVehicleClick?.(vehicle.id)}
              >
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <Avatar className="h-16 w-16 shrink-0 rounded-lg">
                      {vehicle.image_url && (
                        <AvatarImage src={vehicle.image_url} alt={vehicle.name} className="rounded-lg object-cover" />
                      )}
                      <AvatarFallback className="rounded-lg">
                        <Car className="h-6 w-6" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate" title={vehicle.name}>
                            {vehicle.name}
                          </p>
                          <p className="text-2xl font-bold mt-0.5">{fmt(total)}</p>
                        </div>
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {expenseCount} expense{expenseCount !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                        {maintenanceCostPerMile != null && (
                          <span>{fmt(maintenanceCostPerMile)}/mile maintenance</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
